import React, { useState } from 'react';

import { Alert, App, Button, Checkbox, ConfigProvider, Modal, NotificationArgsProps, Space, theme, Typography } from 'antd';

import {
    AimOutlined,
    CheckCircleOutlined,
    DollarCircleOutlined,
    SafetyCertificateOutlined,
    ShareAltOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { GCONDO_BROKER_THEME } from 'internal-constants';
import Authentication from 'lib/Authentication';
import { handleServiceErrorV2, hasServiceErrorV2 } from 'lib/helpers/ServiceHelper';
import { Show } from 'lib/Show';
import { createClientManagerConsent } from 'services/ClientManager.service';

import { SectionTitle } from './SectionTitle';

type Props = { onCancel: () => void };

const POLICY_VERSION = 1 as const;
const POLICY_URL = 'https://files.gcondo.com.br/termo_de_compartilhamento_de_dados_v01.pdf' as const;

export function ClientManagerPendingConsentModal({ onCancel }: Props) {
    const [hasTermsReadAndAgreed, setHasTermsReadAndAgreed] = useState(false);

    const app = App.useApp();

    const { token } = theme.useToken();

    const clientManager = Authentication.getClientManager();

    const isAccountResponsibleUser = Authentication.getUserAccountResponsible();

    const createConsent = async (hasGivenConsent: boolean) => {
        const response = await createClientManagerConsent(
            clientManager.id,
            { 
                consent_given: hasGivenConsent,
                policy_version: POLICY_VERSION,
            }
        );

        if (hasServiceErrorV2(response))
            return handleServiceErrorV2(app, response);

        const args: NotificationArgsProps = hasGivenConsent
            ? { 
                message: 'Consentimento registrado com sucesso!',
                description: 'Em breve você começará a receber cotações.'
            } : {
                message: 'Recusa ao consentimento de dados registrada com sucesso!',
                description: 'Caso mude de ideia, você pode entrar em contato conosco a qualquer momento.'
            };

        app.notification.success(args);

        const { consent } = response.data;

        const updatedClientManager = {
            ...clientManager,
            consent,
        };

        Authentication.setClientManager(updatedClientManager);

        onCancel();
    };

    const onRefuse = () => {
        app.modal.confirm({
            title: 'Tem certeza?',
            content: 'Se você não concordar com o compartilhamento dos dados, não receberá cotações automáticamente.',
            okText: 'Sim',
            cancelText: 'Não',
            onOk: () => createConsent(false),
        });
    };

    const onAccept = () => createConsent(true);

    return (
        <ConfigProvider theme={GCONDO_BROKER_THEME}>
            <Modal
                open
                title="Consentimento para compartilhamento de dados"
                width={600}
                footer={null}
                onCancel={onCancel}
            >
                {/* Padding left misaligns the children text with the modal title, so we don't use it */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: token.paddingContentHorizontal }}>
                    <Typography>
                        <Typography.Paragraph>
                            Para que possamos conectar você com fornecedores, parceiros homologados e facilitar a solicitação de orçamentos, precisamos do seu consentimento para compartilhar algumas informações.
                        </Typography.Paragraph>

                        <Typography.Paragraph>
                            Buscando garantir a você a melhor experiência, destacamos abaixo, de forma resumida, as principais informações:
                        </Typography.Paragraph>

                        <SectionTitle
                            icon={ShareAltOutlined}
                            title="O que será compartilhado?"
                        />
    
                        <ul style={{ paddingLeft: '20px' }}>
                            <li>
                                <Typography.Text strong>Dados do condomínio:</Typography.Text> nome, CNPJ, endereço, número de unidades e características gerais.
                            </li>
                            <li>
                                <Typography.Text strong>Dados da solicitação:</Typography.Text> descrição do serviço ou produto, especificações técnicas, fotos, plantas ou documentos anexados.
                            </li>
                            <li>
                                <Typography.Text strong>Seus dados de contato:</Typography.Text> nome, e-mail e telefone, para que os fornecedores possam entrar em contato.
                            </li>
                        </ul>

                        <Alert
                            showIcon
                            type="info"
                            description="Fique tranquilo! Não compartilhamos dados pessoais de condôminos, como CPF, RG ou dados financeiros."
                            icon={<SafetyCertificateOutlined />}
                            style={{ marginTop: '16px' }}
                        />

                        <SectionTitle
                            icon={AimOutlined}
                            title="Finalidade"
                        />

                        <Typography.Paragraph>
                            Os dados serão utilizados exclusivamente para a elaboração de cotações e propostas comerciais relacionadas à sua solicitação. Não usaremos suas informações para marketing ou repasse a terceiros não envolvidos.
                        </Typography.Paragraph>

                        <SectionTitle
                            icon={TeamOutlined}
                            title="Com quem?"
                        />

                        <Typography.Paragraph>
                            Apenas com fornecedores e parceiros homologados, que seguem nossas políticas de privacidade e a legislação de proteção de dados vigente.
                        </Typography.Paragraph>

                        <SectionTitle
                            icon={DollarCircleOutlined}
                            title="Valores e compromisso"
                        />

                        <Typography.Paragraph>
                            <Typography.Text strong>Não há custos para você.</Typography.Text> O compartilhamento de dados é gratuito e opcional. Você pode revogar o consentimento a qualquer momento, entrando em contato conosco. Além disso, você não tem obrigação de aceitar cotações ou propostas recebidas.
                        </Typography.Paragraph>

                        <SectionTitle
                            icon={CheckCircleOutlined}
                            title="Validade do consentimento"
                        />

                        <Typography.Paragraph>
                            Ao aceitar, você declara ter autorização para representar os condomínios que administra. O consentimento se aplica aos condomínios já cadastrados e aos que forem adicionados posteriormente.
                        </Typography.Paragraph>
                    </Typography>
                </div>
            
                <div style={{
                    paddingTop: 16,
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Show when={isAccountResponsibleUser}>
                        <Space direction="vertical">
                            <Checkbox
                                checked={hasTermsReadAndAgreed}
                                onChange={event => setHasTermsReadAndAgreed(event.target.checked)}
                            >
                                Declaro que li e concordo com o
                                {' '}
                                <Typography.Link
                                    href={POLICY_URL}
                                    target="_blank"
                                >
                                Termo de Compartilhamento de Dados
                                </Typography.Link>
                            </Checkbox>

                            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                Você pode revogar este consentimento a qualquer momento entrando em contato conosco.
                            </Typography.Text>

                            <Space>
                                <Button
                                    key="refuse"
                                    onClick={onRefuse}
                                >
                                    Não, obrigado
                                </Button>

                                <Button
                                    key="accept"
                                    type="primary"
                                    onClick={onAccept}
                                    disabled={!hasTermsReadAndAgreed}
                                >
                                    Sim, quero receber cotações
                                </Button>
                            </Space>
                        </Space>
                    </Show>

                    <Show when={!isAccountResponsibleUser}>
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                            Para aceitar ou recusar o compartilhamento de dados, você precisa ser o responsável pela empresa. Por favor, entre em contato com o administrador da conta.
                        </Typography.Text>
                    </Show>
                </div>
            </Modal>
        </ConfigProvider>
    );
};
